import { IssueStatusIcon } from "@/components/IssueStatusIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { IssuePriority, IssueStatus } from "@/constants/issueConstants";
import { cn, formatCompactRelativeTime } from "@/lib/utils";
import type { Row_issues, Row_projects, Row_users } from "@/types/databaseRows";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export interface IssueInlinePreviewProps {
  issue: Row_issues & {
    assignee?: Row_users;
    project?: Row_projects;
  };
  /** URL to navigate to - uses Link for proper browser link behavior */
  href?: string;
  /** Legacy onClick handler - use href instead when possible */
  onClick?: (issueId: string) => void;
  isActive?: boolean;
  showStatus?: boolean;
  showPriority?: boolean;
  showAssignee?: boolean;
  showProject?: boolean;
  className?: string;
  "data-testid"?: string;
}

// Status badge classes (for alternate display)
const statusBadgeClasses: Record<IssueStatus, string> = {
  backlog: "bg-muted text-muted-foreground",
  todo: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  "in progress": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  "in review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  done: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  canceled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  triage: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
};

// Priority color classes
const priorityColorClasses: Record<IssuePriority, string> = {
  high: "text-red-600 dark:text-red-400",
  medium: "text-orange-600 dark:text-orange-400",
  low: "text-blue-600 dark:text-blue-400",
  not_set: "text-muted-foreground",
};

function normalizeStatus(status: string): IssueStatus {
  // Normalize different representations of status
  const normalized = status.toLowerCase().replace(/_/g, " ").replace(/-/g, " ");
  return normalized as IssueStatus;
}

function formatStatus(status: string): string {
  // Format status for display - lowercase for consistency with tests
  return normalizeStatus(status);
}

function formatPriority(priority: string): string {
  // Format priority for display - lowercase for consistency with tests
  return priority.replace(/_/g, " ").toLowerCase();
}

export default function IssueInlinePreview({
  issue,
  href,
  onClick,
  isActive = false,
  showStatus = true,
  showPriority = false,
  showAssignee = false,
  showProject = false,
  className,
  "data-testid": dataTestId,
}: IssueInlinePreviewProps) {
  const normalizedStatus = useMemo(() => normalizeStatus(issue.status), [issue.status]);
  const priorityColorClass =
    priorityColorClasses[issue.priority as IssuePriority] || "text-muted-foreground";

  const handleClick = () => {
    if (onClick) {
      onClick(issue.id);
    }
  };

  const tooltipContent = useMemo(() => {
    const lines = [
      `[${issue.issue_number}] ${issue.title}`,
      "",
      `Updated: ${issue.updated_at ? formatCompactRelativeTime(issue.updated_at) : "N/A"}`,
      `Created: ${issue.created_at ? formatCompactRelativeTime(issue.created_at) : "N/A"}`,
    ];
    return lines.join("\n");
  }, [issue.issue_number, issue.title, issue.updated_at, issue.created_at]);

  const content = (
    <>
      {/* Status icon using IssueStatusIcon component */}
      <IssueStatusIcon
        status={issue.status}
        className="shrink-0"
        data-testid={dataTestId ? `${dataTestId}-status-icon` : undefined}
      />

      {/* Issue title and metadata */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div
            className="font-medium text-foreground truncate"
            data-testid={dataTestId ? `${dataTestId}-field-title` : undefined}
          >
            {issue.title}
          </div>
        </div>

        {/* Show priority inline */}
        {showPriority && (
          <span
            className={cn("text-xs font-medium shrink-0", priorityColorClass)}
            data-testid={dataTestId ? `${dataTestId}-field-priority` : undefined}
          >
            {formatPriority(issue.priority)}
          </span>
        )}

        {/* Show assignee inline */}
        {showAssignee && (
          <span
            className="text-xs text-muted-foreground shrink-0"
            data-testid={dataTestId ? `${dataTestId}-field-assignee` : undefined}
          >
            {issue.assignee ? issue.assignee.name : "Unassigned"}
          </span>
        )}

        {/* Show project inline */}
        {showProject && issue.project && (
          <span
            className="text-xs text-muted-foreground shrink-0"
            data-testid={dataTestId ? `${dataTestId}-field-project` : undefined}
          >
            {issue.project.name}
          </span>
        )}
      </div>

      {/* Status badge */}
      {showStatus && (
        <span
          className={cn(
            "inline-block px-2 py-1 rounded-md text-xs font-medium shrink-0",
            statusBadgeClasses[normalizedStatus]
          )}
          data-testid={dataTestId ? `${dataTestId}-field-status` : undefined}
        >
          {formatStatus(issue.status)}
        </span>
      )}
    </>
  );

  const wrapperClasses = cn(
    "flex items-center gap-3 p-2 rounded-md transition-colors",
    (href || onClick) && "cursor-pointer hover:bg-accent",
    isActive && "bg-accent",
    className
  );

  // Use Link for proper browser link behavior when href is provided
  if (href) {
    return (
      <Tooltip delayDuration={300} disableHoverableContent>
        <TooltipTrigger asChild>
          <Link to={href} className={wrapperClasses} data-testid={dataTestId}>
            {content}
          </Link>
        </TooltipTrigger>
        <TooltipContent
          className="whitespace-pre-wrap text-left pointer-events-none"
          data-testid={dataTestId ? `${dataTestId}-tooltip` : undefined}
        >
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Fall back to div with onClick for legacy usage
  return (
    <Tooltip delayDuration={300} disableHoverableContent>
      <TooltipTrigger asChild>
        <div onClick={handleClick} className={wrapperClasses} data-testid={dataTestId}>
          {content}
        </div>
      </TooltipTrigger>
      <TooltipContent
        className="whitespace-pre-wrap text-left pointer-events-none"
        data-testid={dataTestId ? `${dataTestId}-tooltip` : undefined}
      >
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
}
